import React, { useEffect, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import Button from '../../components/buttons/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import InputField from '../../components/inputFields/InputField';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import { postRawMaterialInventory, postRawMaterialInventoryTransaction } from '../../services/inventory';
import { toast } from 'react-toastify';
import { getUserId } from '../../helpers/local_storage_helpers';

const RawItemsInventoryForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);

    const categories = [
        { value: 'Electrical', label: 'Electrical' },
        { value: 'Electronics', label: 'Electronics' },
        { value: 'PCB', label: 'PCB' },
        { value: 'Mechanical', label: 'Mechanical' },
        { value: 'Plastic', label: 'Plastic' },
        { value: 'Metal', label: 'Metal' },
        { value: 'Wire', label: 'Wire' },
        { value: 'Other', label: 'Other' }
    ];

    const units = [
        { value: 'pcs', label: 'Pieces' },
        { value: 'kg', label: 'Kilograms' },
        { value: 'g', label: 'Grams' },
        { value: 'l', label: 'Liters' },
        { value: 'ml', label: 'Milliliters' },
        { value: 'm', label: 'Meters' },
        { value: 'cm', label: 'Centimeters' },
        { value: 'mm', label: 'Millimeters' },
        { value: 'in', label: 'Inches' },
        { value: 'ft', label: 'Feet' },
        { value: 'yd', label: 'Yards' },
        { value: 'mi', label: 'Miles' },
    ];

    useEffect(() => {
        if (location.state) {
            setRowData(location.state.item);
        }
    }, [location]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        let data = rowData ? {
            raw_material: rowData.id,
            quantity: values.quantity,
            price: values.price,
            location: values.location,
            supplier: values.supplier,
            notes: values.notes,
            unit: rowData.unit,
            added_by: getUserId(),
        } : {
            name: values.name,
            sku: values.sku,
            category: values.category,
            description: values.description,
            threshold: values.threshold,
            unit: values.unit,
            added_by: getUserId(),
        };

        try {
            const response = rowData ? await postRawMaterialInventoryTransaction(data) : await postRawMaterialInventory(data);

            if (response?.status === 201) {
                toast.success("Item Added successfully!");
                navigate(-1);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Inventory', link: '/inventory/home' },
            { name: 'Raw Items', link: '/inventory/raw-items' },
            { name: rowData ? 'Update Item' : 'Add Item' },
        ]} />
        <div className="mt-8">
            <Formik
                initialValues={rowData ? {
                    price: "",
                    location: "",
                    notes: "",
                    supplier: "",
                    quantity: "",
                    unit: "",
                } : {
                    name: "",
                    sku: "",
                    description: "",
                    threshold: 0,
                    category: "Electrical",
                }}
                validationSchema={Yup.object(rowData ? {
                    quantity: Yup.number().required("Quantity is required"),
                } : {
                    name: Yup.string().required("Name is required"),
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values }) => (
                    <Form>
                        <div className="border border-gray-300 mt-5 p-6 rounded-lg bg-gray-50">
                            {/* Hide all other sections when editing */}
                            {!rowData && (
                                <>
                                    {/* Basic Information Section */}
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                                            Basic Information
                                        </h3>
                                        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                                            <InputField
                                                label="Item Name"
                                                name="name"
                                                type="text"
                                                placeholder="Enter item name"
                                                required
                                            />
                                            <InputField
                                                label="SKU"
                                                name="sku"
                                                type="text"
                                                placeholder="Enter SKU"
                                            />
                                            <FormikDropdown
                                                label="Category"
                                                name="category"
                                                placeholder="Select category"
                                                options={categories}
                                            />
                                            <FormikDropdown
                                                label="Unit"
                                                name="unit"
                                                placeholder="Select unit"
                                                needSearch
                                                options={units}
                                            />
                                            <InputField
                                                label="Threshold"
                                                name="threshold"
                                                type="number"
                                                step="any"
                                                placeholder="Enter threshold"
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <InputField
                                                label="Description"
                                                name="description"
                                                type="text"
                                                placeholder="Enter item description"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Inventory Details Section - Only visible in editing mode */}
                            {rowData && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                                        Inventory Details | {rowData.name}
                                    </h3>
                                    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                                        <InputField
                                            label="Quantity"
                                            name="quantity"
                                            step="any"
                                            type="number"
                                            placeholder="Enter quantity"
                                            required
                                        />
                                        <InputField
                                            label="Price"
                                            name="price"
                                            step="any"
                                            type="number"
                                            placeholder="Enter price"
                                        />

                                        <FormikDropdown
                                            label="Location"
                                            name="location"
                                            placeholder="Select location"
                                            options={[
                                                { value: "Production House", label: "Production House" },
                                                { value: "Office", label: "Office" }
                                            ]}
                                        />
                                        <InputField
                                            label="Supplier"
                                            name="supplier"
                                            type="text"
                                            placeholder="Enter supplier name"
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <InputField
                                            label="Notes"
                                            name="notes"
                                            type="text"
                                            placeholder="Enter additional notes"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                                <Button
                                    type="button"
                                    className="text-primary bg-white hover:bg-gray-50 font-medium rounded-lg px-6 py-2.5 border border-primary transition-colors"
                                    onClick={() => handleDiscardChanges(resetForm)}
                                    disabled={isSubmitting}
                                >
                                    Discard Changes
                                </Button>
                                <Button
                                    type="submit"
                                    className="text-white bg-primary hover:bg-primary-dark font-medium rounded-lg px-6 py-2.5 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Saving..." : "Save Item"}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    </>);
};

export default RawItemsInventoryForm; 